import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import MandatorySign from "../Components/MandatorySign";
import { useLanguage } from "../Translations/LenguageContext";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const NewPropertyModal = ({ account_id, show, handleClose, fetchRecords }) => {
    const { t } = useLanguage()
    const { org_id } = useParams()
    const navigate = useNavigate()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });

    const [formData, setFormData] = useState({
        name: "",
        base_url: "",
        domain: "",
        deployment: false
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: value
            };

            if (name === "base_url") {
                try {
                    const url = new URL(
                        value.includes("://") ? value : `https://${value}`
                    );

                    updated.domain = url.hostname
                        .toLowerCase()
                        .replace(/^www\./, "");
                } catch {
                    updated.domain = "";
                }
            }

            return updated;
        });
    };

    const handleDeploymentChange = (value) => {
        setFormData(prev => ({
            ...prev,
            deployment: value
        }));
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            setErrorMessage({ message: t.properties.name_required, display: true });
            return;
        }

        if (!formData.base_url.trim()) {
            setErrorMessage({ message: t.properties.base_url_required, display: true });
            return;
        }

        if (!formData.domain.trim()) {
            setErrorMessage({ message: t.properties.domain_required, display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/new_property`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    name: formData.name,
                    base_url: formData.base_url,
                    domain: formData.domain,
                    deployment: formData.deployment
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message == "OK") {
                navigate(data.id)
            }

        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setFormData({ name: "", base_url: "" });
        setErrorMessage({ message: "", display: false });
        setLoadingSave(false);
    };

    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">{t.properties.new_title}</p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    <div className="label_input">
                        <label className="input_label">{t.name} <MandatorySign message={t.mandatory_field} /></label>
                        <input
                            type="text"
                            name="name"
                            maxLength={100}
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Ex: My website"
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>

                    <div className="label_input">
                        <label className="input_label">{t.properties.base_url} <MandatorySign message={t.mandatory_field} /></label>
                        <input
                            type="text"
                            name="base_url"
                            value={formData.base_url}
                            onChange={handleChange}
                            placeholder="https://www.mywebsite.com"
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>

                    <div className="label_input">
                        <label className="input_label">{t.properties.domain} <MandatorySign message={t.mandatory_field} /></label>
                        <input
                            type="text"
                            name="domain"
                            value={formData.domain}
                            onChange={handleChange}
                            placeholder="mywebsite.com"
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>

                    <div className="label_input">
                        <label className="input_label">
                            {t.properties.deployment} <MandatorySign message={t.mandatory_field} />
                        </label>

                        <Dropdown>
                            <Dropdown.Toggle
                                as="div"
                                className="register_form_first fade-in"
                                style={{ cursor: "pointer" }}
                            >
                                {formData.deployment ? t.properties.enabled : t.properties.disabled}
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div className="dropdown_container fade-in">
                                    <div className="scrollable_dropdown_cont">
                                        <button
                                            type="button"
                                            className="settings_link"
                                            onClick={() => handleDeploymentChange(true)}
                                        >
                                            <i>
                                                {formData.deployment
                                                    ? <MdOutlineCheckCircle />
                                                    : <MdOutlineCircle />
                                                }
                                            </i>
                                            {t.properties.enabled}
                                        </button>

                                        <button
                                            type="button"
                                            className="settings_link"
                                            onClick={() => handleDeploymentChange(false)}
                                        >
                                            <i>
                                                {!formData.deployment
                                                    ? <MdOutlineCheckCircle />
                                                    : <MdOutlineCircle />
                                                }
                                            </i>
                                            {t.properties.disabled}
                                        </button>
                                    </div>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <p className="warning_source_message fade-in">
                        {t.properties.verify_domain_warn} <br />
                        {formData.deployment && (
                            t.properties.deployment_warn
                        )}
                    </p>
                </div>
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? <Spinner loading={true} /> : t.save}
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default NewPropertyModal;